import React, { useState, useEffect } from 'react';
import { useSalesRealTime } from '../../hooks/useWebSocket';
import { analyticsService } from '../../services/analyticsService';

interface RealTimeSalesUpdatesProps {
  onDataUpdate?: (data: any) => void;
}

const RealTimeSalesUpdates: React.FC<RealTimeSalesUpdatesProps> = ({ onDataUpdate }) => {
  const { connected, error, salesUpdates, notifications, clearNotifications } = useSalesRealTime(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [realtimeMetrics, setRealtimeMetrics] = useState({
    todayRevenue: 0,
    todayBookings: 0,
    activeBookings: 0,
    lastUpdate: new Date()
  });

  // Update metrics when new sales updates arrive
  useEffect(() => {
    if (salesUpdates.length > 0) {
      const latestUpdate = salesUpdates[0];
      
      if (latestUpdate.type === 'booking_created') {
        setRealtimeMetrics(prev => ({
          ...prev,
          todayBookings: prev.todayBookings + 1,
          activeBookings: prev.activeBookings + 1,
          lastUpdate: new Date()
        }));
      } else if (latestUpdate.type === 'booking_completed') {
        setRealtimeMetrics(prev => ({
          ...prev,
          todayRevenue: prev.todayRevenue + (latestUpdate.data.revenue || 0),
          activeBookings: Math.max(0, prev.activeBookings - 1),
          lastUpdate: new Date()
        }));
      } else if (latestUpdate.type === 'revenue_update') {
        setRealtimeMetrics(prev => ({
          ...prev,
          todayRevenue: latestUpdate.data.revenue || prev.todayRevenue,
          lastUpdate: new Date()
        }));
      }

      // Notify parent component of data updates
      if (onDataUpdate) {
        onDataUpdate(latestUpdate);
      }
    }
  }, [salesUpdates, onDataUpdate]);

  const getConnectionStatusColor = () => {
    if (error) return 'text-red-600';
    if (connected) return 'text-green-600';
    return 'text-yellow-600';
  };

  const getConnectionStatusText = () => {
    if (error) return 'Connection Error';
    if (connected) return 'Connected';
    return 'Connecting...';
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  return (
    <div className="space-y-4">
      {/* Connection Status and Real-time Metrics */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Real-time Sales Monitor</h3>
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <div className={`w-2 h-2 rounded-full mr-2 ${
                connected ? 'bg-green-500' : error ? 'bg-red-500' : 'bg-yellow-500'
              }`}></div>
              <span className={`text-sm font-medium ${getConnectionStatusColor()}`}>
                {getConnectionStatusText()}
              </span>
            </div>
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM11 19H6a2 2 0 01-2-2V7a2 2 0 012-2h5m5 0v5" />
              </svg>
              {notifications.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {notifications.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Real-time Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-green-600">Today's Revenue</p>
                <p className="text-2xl font-bold text-green-900">
                  {analyticsService.formatCurrency(realtimeMetrics.todayRevenue)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-blue-600">Today's Bookings</p>
                <p className="text-2xl font-bold text-blue-900">{realtimeMetrics.todayBookings}</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-yellow-600">Active Bookings</p>
                <p className="text-2xl font-bold text-yellow-900">{realtimeMetrics.activeBookings}</p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-purple-600">Last Update</p>
                <p className="text-sm font-bold text-purple-900">
                  {formatTimeAgo(realtimeMetrics.lastUpdate)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notifications Panel */}
      {showNotifications && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-gray-900">Live Notifications</h4>
            <button
              onClick={clearNotifications}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear All
            </button>
          </div>
          
          {notifications.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No new notifications</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {notifications.map((notification, index) => (
                <div
                  key={index}
                  className="flex items-center p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-shrink-0">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm text-gray-900">{notification}</p>
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatTimeAgo(new Date(Date.now() - index * 30000))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recent Activity Feed */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Recent Sales Activity</h4>
        
        {salesUpdates.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No recent activity</p>
        ) : (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {salesUpdates.slice(0, 10).map((update, index) => (
              <div key={index} className="flex items-center justify-between p-3 border-l-4 border-blue-500 bg-blue-50">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    {update.type === 'booking_created' && (
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    )}
                    {update.type === 'booking_completed' && (
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    {update.type === 'revenue_update' && (
                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">
                      {update.type === 'booking_created' && 'New Booking Created'}
                      {update.type === 'booking_completed' && 'Booking Completed'}
                      {update.type === 'revenue_update' && 'Revenue Updated'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {update.data.bookingId && `Booking: ${update.data.bookingId}`}
                      {update.data.revenue && ` • Revenue: ${analyticsService.formatCurrency(update.data.revenue)}`}
                      {update.data.vehicleId && ` • Vehicle: ${update.data.vehicleId}`}
                    </p>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {formatTimeAgo(new Date(update.data.timestamp))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Connection Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Real-time Connection Error</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <p className="mt-1 text-xs text-red-600">
                Real-time updates are currently unavailable. Data will refresh automatically when connection is restored.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RealTimeSalesUpdates;